import { Compass, Heart, Zap, Users, Trophy, Rocket } from "lucide-react";

const TEAM = [
  { name: "Aria Chen", role: "CEO & Co-founder", avatar: "https://images.unsplash.com/photo-1600603406200-5b2a104684ac" },
  { name: "Marcus Oduya", role: "Head of Community", avatar: "https://images.unsplash.com/photo-1581841064838-a470c740e8ee" },
  { name: "Priya Shah", role: "Head of Product", avatar: "https://images.unsplash.com/photo-1556761175-4b46a572b786" },
];

const VALUES = [
  { i: Heart, t: "Creators first", d: "Every product decision starts with: does this help creators ship better work?" },
  { i: Zap, t: "Ship, don't stall", d: "We ship weekly. Feedback becomes features fast." },
  { i: Users, t: "Community over algorithm", d: "Our discovery is human-curated. No opaque ranking games." },
  { i: Trophy, t: "Trust as a feature", d: "Verification, transparency, and fair fees. Not optional." }
];

const TIMELINE = [
  { year: "2021", t: "Founded in San Francisco", d: "Three co-founders, one manifesto: crowdfunding shouldn't feel like a slot machine." },
  { year: "2022", t: "First 100 campaigns funded", d: "$4.2M raised in our first year. 91% delivery rate — an industry high." },
  { year: "2023", t: "Series A led by community", d: "We raised our own Series A on Dwinov. 12,000 backers became owners." },
  { year: "2024", t: "1M backers, 120 countries", d: "Global logistics launched. Localized in 14 languages." },
  { year: "2026", t: "The next chapter", d: "Onchain payouts, creator collectives, and a new studio product." }
];

const About = () => {
  return (
    <div data-testid="about-page">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-warm-200 bg-gradient-to-b from-slate-50 to-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
          <div className="max-w-3xl">
            <span className="text-sm font-medium text-saffron-600">About Dwinov</span>
            <h1 className="mt-2 font-display text-5xl md:text-7xl font-semibold tracking-tight text-navy-800 leading-[1.05]">
              We fund the future — one idea at a time.
            </h1>
            <p className="mt-6 text-lg text-navy-600 leading-relaxed">
              Dwinov is a home for the people building things worth backing. We're currently in active development, opening to a founding community of creators and backers who believe in shipping ideas the right way — with transparency, community, and craft.
            </p>
          </div>
        </div>
      </section>

      {/* Mission / Vision */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-24">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="rounded-3xl border border-warm-200 p-10 bg-white">
            <div className="inline-grid place-items-center h-11 w-11 rounded-xl bg-saffron-50 text-saffron-600">
              <Compass className="h-5 w-5" />
            </div>
            <h2 className="mt-6 font-display text-3xl font-semibold text-navy-800 tracking-tight">Mission</h2>
            <p className="mt-3 text-navy-600 leading-relaxed">
              Empower every creator, founder, and changemaker to raise funding through a community that shows up — from first pledge to final ship.
            </p>
          </div>
          <div className="rounded-3xl border border-warm-200 p-10 bg-navy-800 text-white">
            <div className="inline-grid place-items-center h-11 w-11 rounded-xl bg-saffron-500/20 text-saffron-500">
              <Rocket className="h-5 w-5" />
            </div>
            <h2 className="mt-6 font-display text-3xl font-semibold tracking-tight">Vision</h2>
            <p className="mt-3 text-slate-300 leading-relaxed">
              A world where the best ideas find capital and community — regardless of geography, network, or gatekeeper.
            </p>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="bg-cream-100/70 border-y border-warm-200">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-24">
          <div className="max-w-2xl">
            <span className="text-sm font-medium text-saffron-600">Core values</span>
            <h2 className="mt-2 font-display text-4xl md:text-5xl font-semibold tracking-tight text-navy-800">The principles we ship by.</h2>
          </div>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {VALUES.map((v, i) => (
              <div key={i} className="rounded-2xl border border-warm-200 bg-white p-6 hover:border-saffron-200 hover:shadow-md transition-[box-shadow,border-color]">
                <div className="inline-grid place-items-center h-10 w-10 rounded-xl bg-saffron-50 text-saffron-600">
                  <v.i className="h-5 w-5" />
                </div>
                <h3 className="mt-4 font-display font-semibold text-navy-800">{v.t}</h3>
                <p className="mt-2 text-sm text-navy-600 leading-relaxed">{v.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
        <div className="text-center max-w-2xl mx-auto">
          <span className="text-sm font-medium text-saffron-600">Timeline</span>
          <h2 className="mt-2 font-display text-4xl md:text-5xl font-semibold tracking-tight text-navy-800">From idea to industry standard.</h2>
        </div>
        <div className="mt-16 relative">
          <div className="absolute left-6 top-2 bottom-2 w-px bg-slate-200" />
          <div className="space-y-10">
            {TIMELINE.map((t) => (
              <div key={t.year} className="pl-16 relative">
                <div className="absolute left-0 top-0 h-12 w-12 rounded-full bg-white border-2 border-saffron-600 grid place-items-center font-display font-semibold text-saffron-600 text-sm">
                  {t.year.slice(2)}
                </div>
                <div className="text-xs font-medium text-navy-500 uppercase tracking-wide">{t.year}</div>
                <h3 className="mt-1 font-display text-xl font-semibold text-navy-800 tracking-tight">{t.t}</h3>
                <p className="mt-2 text-navy-600 leading-relaxed">{t.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="bg-cream-100/70 border-t border-warm-200">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-24">
          <div className="max-w-2xl">
            <span className="text-sm font-medium text-saffron-600">The team</span>
            <h2 className="mt-2 font-display text-4xl md:text-5xl font-semibold tracking-tight text-navy-800">Small team, big ambition.</h2>
          </div>
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
            {TEAM.map((c) => (
              <div key={c.name} className="rounded-2xl border border-warm-200 bg-white overflow-hidden group hover:-translate-y-0.5 hover:shadow-lg transition-[transform,box-shadow]">
                <div className="aspect-[4/3] overflow-hidden">
                  <img src={c.avatar} alt={c.name} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-5">
                  <div className="font-display font-semibold text-navy-800">{c.name}</div>
                  <div className="text-sm text-navy-500 mt-0.5">{c.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
